import {Component, inject, OnDestroy, OnInit, computed, signal} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {PatientService} from '../../patients/services/patient.service';
import {ProblemItemAddCardComponent} from '../problem-item-add-card/problem-item-add-card.component';
import {MatDialog} from '@angular/material/dialog';
import {CoreService} from '../../../core/services/core.service';
import {PatientEvent, PatientEventsService} from '../../patients/services/patient-events.service';
import {Patient, Problem, ProblemListRepositoryService, UserSettings, WebSocketService, Events} from '@xmedical/core';
import {ProblemAddedEvent, ProblemDisplayItem, ProblemRemovedEvent} from '@xmedical/shared';

@Component({
    selector: 'patient-problem-widget',
    templateUrl: './problem-widget.component.html',
    styleUrls: ['./problem-widget.component.scss'],
    standalone: false
})
export class ProblemWidgetComponent implements OnInit, OnDestroy {

  error = '';
  loading = false;
  settings: UserSettings;
  problems: Problem[] = [];
  private subscriptions: Subscription[] = [];
  private channelName = '';
  private readonly eventName = Events.ProblemListProblemListChanged;

  private matDialog = inject(MatDialog);
  private problemsRepository = inject(ProblemListRepositoryService);
  private patientService = inject(PatientService);
  private coreService = inject(CoreService);
  private pushService = inject(WebSocketService);
  private patientEventService = inject(PatientEventsService);

  // Signals for shared widget
  isProcessing = signal(false);
  private problemsSignal = signal<Problem[]>([]);

  // Permission computed signals
  canAdd = computed(() => this.coreService.hasPermission('Clinical.ProblemListing.Write'));
  canDelete = computed(() => this.coreService.hasPermission('Clinical.ProblemListing.Write'));

  // Map problems to display items for shared widget
  problemItems = computed<ProblemDisplayItem[]>(() =>
    this.problemsSignal().map(item => ({
      id: item.id,
      problem: item.problem,
      comment: item.comment,
      lastModifiedDate: item.lastModifiedDate,
      lastModifiedUser: item.lastModifiedUser
    }))
  );

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasProblems(): boolean {
    return this.problems.length > 0;
  }

  get patientId(): number {
    return this.patientService.currentPatient.id;
  }

  get patient(): Patient {
    return this.patientService.currentPatient;
  }

  searchProblems$ = (query: string): Observable<Problem[]> => {
    return this.problemsRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.channelName = this.patientService.currentPatientEventsChannelName;
    this.settings = this.coreService.settings();
    this.subscriptions.push(this.patientEventService.currentPatient$.subscribe(() => this.loadPatientProblemList()));
    this.subscriptions.push(this.patientService.patientProblems$.subscribe(problems => {
      this.problems = [...problems];
      this.problemsSignal.set(this.problems);
    }));
    this.subscriptions.push(this.patientEventService.events$.pipe(filter(e => e.type === 'problem_item_deleted')).subscribe(() => this.loadPatientProblemList()));
    this.pushService.subscribe(this.channelName, this.eventName, data => {
      console.log('ProblemWidgetComponent: pushMessageReceived', data);
      this.loadPatientProblemList();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pushService.unsubscribe(this.channelName, this.eventName);
  }

  onAddNewItem(searchTerm: string) {
    const dialogRef = this.matDialog.open(ProblemItemAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
      data: { searchTerm }
    });

    this.subscriptions.push(dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.onItemSelected(result);  // Direct add with full item (has ID)
      }
    }));
  }

  onItemAdded(event: ProblemAddedEvent) {
    const problem = event.item;
    problem.comment = event.comment;
    this.onItemSelected(problem);
  }

  onItemSelected(problem: Problem) {
    problem.createdUser = this.coreService.currentUser;
    problem.lastModifiedUser = this.coreService.currentUser;
    problem.createdDate = new Date();
    problem.lastModifiedDate = new Date();
    this.problems.push(problem);
    this.problemsSignal.set([...this.problems]); // Update signal for visual refresh
    this.addProblemToPatient(problem);
    // Note: cancelSearch is now handled internally by the shared widget
  }

  onItemRemoved(event: ProblemRemovedEvent) {
    const problem = this.problems.find(p => p.id === event.item.id);
    if (problem) {
      this.isProcessing.set(true);
      this.problemsRepository.removeProblemFromPatient(this.patientId, problem.id).subscribe({
        next: () => {
          this.problems = this.problems.filter(p => p.id !== problem.id);
          this.problemsSignal.set(this.problems);
          this.patientEventService.emitEvent(new PatientEvent('problem_item_deleted'));
          this.isProcessing.set(false);
        },
        error: (error) => {
          this.coreService.showErrorToast('Could not remove problem item');
          this.isProcessing.set(false);
        }
      });
    }
  }

  private loadPatientProblemList() {
    if (!this.patientService.hasPatientDetails) {
      return;
    }
    this.patientService.loadPatientProblems();
  }

  private addProblemToPatient(problem: Problem) {
    this.error = '';
    this.subscriptions.push(this.problemsRepository.addProblemToPatient(this.patientId, problem).subscribe((newId) => {
      this.patientEventService.emitEvent(new PatientEvent('problem_item_added'));
      problem.id = newId;
    }, error => {
      this.error = 'Could not add new problem item to this patient';
      this.loadPatientProblemList();
    }));
  }

}
